import { useCallback, useMemo } from 'react'

import {
  ContentModuleType,
  Event,
  EventRecurrence,
} from '@tribeplatform/gql-client/types'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { VirtualList } from '@tribeplatform/react-ui-kit/VirtualList'

import {
  useContentModuleUtils,
  useContentViewMode,
} from '../../../common/index.js'
import { dayjs } from '../../../common/lib/dayjs.js'
import { Link } from '../../../Link/Link.js'

import {
  RecurrenceItem,
  RecurrenceItemLoadingSkeleton,
} from './RecurrenceItem.js'
import { useBidirectionalEventRecurrences } from './useBidirectionalEventRecurrences.js'

const ESTIMATED_ITEM_SIZE = 90
const HEADER_HEIGHT = 40
const OVERSCAN = 10

interface Props {
  event: Event
  onClose: () => void
}

export const RecurrenceList = ({ event, onClose }: Props) => {
  const { isPageView } = useContentViewMode()
  const {
    recurrences,
    initialIndex,
    isLoading,
    hasPastRecurrences,
    isFetchingPast,
    isFetchingFuture,
    fetchPastRecurrences,
    fetchFutureRecurrences,
  } = useBidirectionalEventRecurrences({
    event,
    fields: {
      authMemberProps: {
        eventRegistration: 'all',
      },
      physicalLocations: 'basic',
    },
  })
  const { linkToContentModal } = useContentModuleUtils()

  const handleHeadReached = useCallback(() => {
    fetchPastRecurrences()
  }, [fetchPastRecurrences])

  const handleTailReached = useCallback(() => {
    fetchFutureRecurrences()
  }, [fetchFutureRecurrences])

  const getItemKey = useCallback(
    (index: number) => {
      return recurrences?.at(index)?.id ?? `item-${index}`
    },
    [recurrences],
  )

  const itemComparator = useCallback(
    (itemA: EventRecurrence, itemB: EventRecurrence) => {
      return itemA && itemB && itemA.id === itemB.id
    },
    [],
  )
  const nextFutureEvent = useMemo(() => {
    return recurrences?.find(r => dayjs(r.startTime).isAfter(dayjs()))
  }, [recurrences])

  const renderItem = useCallback(
    (recurrence: EventRecurrence) => {
      const linkTo = isPageView
        ? recurrence.relativeUrl
        : linkToContentModal({
            spaceRelativeUrl: event.space?.relativeUrl,
            contentId: recurrence.id,
            contentType: ContentModuleType.event,
          })

      const isFirstFutureEvent =
        recurrence.id === nextFutureEvent?.id && !hasPastRecurrences

      return (
        <div className="p-1">
          <Link key={recurrence.id} href={linkTo} onClick={onClose}>
            <RecurrenceItem
              recurrence={recurrence}
              isSelected={recurrence.id === event.id}
              isNextAvailable={isFirstFutureEvent}
            />
          </Link>
        </div>
      )
    },
    [
      event.id,
      event.space?.relativeUrl,
      hasPastRecurrences,
      isPageView,
      linkToContentModal,
      nextFutureEvent?.id,
      onClose,
    ],
  )

  const headerComponent = useMemo(() => {
    if (isFetchingPast) {
      return (
        <div className="flex items-center justify-center py-2">
          <SvgIcon className="animate-spin" size="lg" name="spinner" />
        </div>
      )
    }
    if (hasPastRecurrences) {
      return <div className="h-1" />
    }
    return null
  }, [isFetchingPast, hasPastRecurrences])

  if (isLoading) {
    return <RecurrenceListLoadingSkeleton />
  }

  if (recurrences.length === 0) {
    return null
  }

  return (
    <div className="relative h-full w-full">
      <VirtualList
        items={recurrences}
        getItemKey={getItemKey}
        itemRenderer={renderItem}
        itemComparator={itemComparator}
        estimateSize={() => ESTIMATED_ITEM_SIZE}
        overscan={OVERSCAN}
        onHeadReached={handleHeadReached}
        onTailReached={handleTailReached}
        initialIndex={initialIndex}
        headerHeight={HEADER_HEIGHT}
        headerInnerComponent={headerComponent}
      />
      {isFetchingFuture && (
        <div className="flex items-center justify-center py-2">
          <SvgIcon className="animate-spin" size="lg" name="spinner" />
        </div>
      )}
    </div>
  )
}

const RecurrenceListLoadingSkeleton = () => {
  return (
    <div className="animate-pulse flex flex-col gap-y-2">
      {[...Array(5)].map((_e, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <RecurrenceItemLoadingSkeleton key={i} />
      ))}
    </div>
  )
}
